<script setup lang="ts">
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import List from '@shared/components/List.vue';
    import { inject, reactive, computed } from 'vue';
    import { SegmentAccess } from '../Segments.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const segmentAccess: SegmentAccess = injectorAccess.injector.get(SegmentAccess);

    const state = reactive<{
        isLoadingPreview: boolean;
        formReady: boolean;
        contactCount: number | null;
        showContactModal: boolean;
        selectedContactId: string | null;
    }>({
        isLoadingPreview: false,
        formReady: false,
        contactCount: null,
        showContactModal: false,
        selectedContactId: null,
    });

    const allLoaded = computed(() => {
        return segmentAccess.state.segmentPreview?.Result?.length === segmentAccess.state.backgroundCalculationResult?.PagedResult?.Count;
    });

    // Add computed properties for background calculation state
    const isCalculating = computed(() => segmentAccess.state.isBackgroundCalculating);
    const hasResults = computed(() => segmentAccess.state.backgroundCalculationResult !== null);

    const load = (filter: string) => {
        if (filter) {
            // If there's a filter, fall back to the original implementation
            segmentAccess.getSegmentPreview(filter);
        } else {
            // If background calculation was already done, don't start again
            if (!hasResults.value && !isCalculating.value) {
                // Start background calculation
                segmentAccess.startBackgroundCalculation().catch((error) => {
                    console.error('Error starting background calculation:', error);
                });
            }
        }
    };

    const loadMore = () => {
        segmentAccess.loadMoreContacts();
    };
</script>

<template>
    <List
        itemId="ContactId"
        :perPage="100"
        :items="segmentAccess.state.segmentPreview?.Result"
        :all-loaded="allLoaded"
        :loading="state.isLoadingPreview || isCalculating || segmentAccess.state.isBackgroundCalculating"
        :server-side-filter="true"
        :filterRateLimit="500"
        @load-more="loadMore"
        @update:filter="(newVal) => load(newVal)">
        <template #item="{ item }">
            <div class="flex-1">
                <h2 class="h4">
                    <a href="javascript:void(0);" @click="$emit('newContactId', item.ContactId)">
                        {{ item.Name ? item.Name : $Resources.NoContactNamePlaceholder }}
                    </a>
                </h2>

                <div class="d-flex flex-wrap col-gap-lg">
                    <p v-if="item.Email">
                        <span>{{ item.Email }}</span>

                        <span class="unsubscribed-status m-sm-l" v-if="!item.IsActive">
                            <span class="glyphicon glyphicon-ban-circle m-xs-r"></span>
                            <small>{{ $Resources.UnsubscribedStatus }}</small>
                        </span>

                        <span class="bounced-status m-sm-l" v-if="item.HasBounced">
                            <span class="glyphicon glyphicon-circle-arrow-left m-xs-r"></span>
                            <small>{{ $Resources.BouncedStatus }}</small>
                        </span>
                    </p>

                    <p v-if="item.SmsNumber">
                        <span>{{ item.SmsNumber }}</span>
                        <span class="unsubscribed-status m-sm-l" v-if="!item.IsSmsActive">
                            <span class="glyphicon glyphicon-ban-circle m-xs-r"></span>
                            <small>{{ $Resources.UnsubscribedStatus }}</small>
                        </span>
                        <span class="bounced-status m-sm-l" v-if="item.HasSmsBounced">
                            <span class="glyphicon glyphicon-circle-arrow-left m-xs-r"></span>
                            <small>{{ $Resources.BouncedStatus }}</small>
                        </span>
                    </p>
                </div>

                <p class="meta">
                    <span v-if="item.Created" class="glyphicon glyphicon-time"></span>
                    {{ $Resources.GenericCreatedLabel }}
                    <span>{{ formatDateTime(item.Created) }}</span>
                </p>
            </div>
        </template>
    </List>
</template>

<style scoped lang="less"></style>

<style lang="less"></style>
